import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { getProjectById } from "../../services/projectService";
import { sendApprovalLink } from "../../services/discussionService";
import {
  getNotesByProjectId,
  addNote,
  updateNote,
  deleteNote,
} from "../../services/noteService";
import { formatDate } from "../../utils/formatDate";
import { formatCurrency } from "../../utils/formatCurrency";
import { updateTaskStatus } from "../../services/taskService";
import { updateBugStatus } from "../../services/bugService";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  PencilSquareIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [taskFilter, setTaskFilter] = useState("pending");
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [filteredBugs, setFilteredBugs] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [editingNote, setEditingNote] = useState(null);
  const [notes, setNotes] = useState([]);
  const [showDesc, setShowDesc] = useState(false);
  const [bugStatusFilter, setBugStatusFilter] = useState("open");

  const milestoneTotal = useMemo(() => {
    return (project?.paymentMilestones || []).reduce(
      (sum, m) => sum + parseFloat(m.amount || 0),
      0,
    );
  }, [project]);

  const extraTotal = useMemo(() => {
    return (project?.extraCharges || []).reduce(
      (sum, e) => sum + parseFloat(e.amount || 0),
      0,
    );
  }, [project]);

  const paymentTotal = useMemo(() => {
    return (project?.payments || []).reduce(
      (sum, p) => sum + parseFloat(p.amount || 0),
      0,
    );
  }, [project]);

  const totalExpected = milestoneTotal + extraTotal;
  const pending = totalExpected - paymentTotal;

  useEffect(() => {
    async function fetchProject() {
      try {
        const data = await getProjectById(id);

        setProject(data);

        // ---- TASK FILTER ----
        if (data.tasks && data.tasks.length > 0) {
          const taskList =
            taskFilter === "all"
              ? data.tasks
              : data.tasks.filter((task) => task.status === taskFilter);

          setFilteredTasks(taskList);
        } else {
          setFilteredTasks([]);
        }

        // ---- BUG FILTER ----
        if (data.bugs && data.bugs.length > 0) {
          const bugsList =
            bugStatusFilter === "all"
              ? data.bugs
              : data.bugs.filter((bug) => bug.status === bugStatusFilter);

          setFilteredBugs(bugsList);
        } else {
          setFilteredBugs([]);
        }

        setNotes(data.notes || []);
      } catch (err) {
        setError("Failed to load project.");
      } finally {
        setLoading(false);
      }
    }

    fetchProject();
  }, [id, taskFilter, bugStatusFilter]);

  const handleAddOrUpdateNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;

    const payload = {
      note: noteText.trim(),
      projectId: id,
    };

    if (editingNote) {
      await updateNote({ ...payload, id: editingNote.id });
    } else {
      await addNote(payload);
    }

    const updatedNotes = await getNotesByProjectId(id);
    setNotes(updatedNotes);
    setNoteText("");
    setEditingNote(null);
  };

  const handleSendApproval = async (discussionId) => {
    try {
      const res = await sendApprovalLink(discussionId);

      if (res.success) {
        // Open WhatsApp automatically
        window.open(res.whatsappLink, "_blank");
        alert("Approval link sent successfully");
      } else {
        alert(res.message || "Failed to send approval link");
      }
    } catch (err) {
      alert("Something went wrong");
    }
  };
  const handleEdit = (note) => {
    setNoteText(note.note);
    setEditingNote(note);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this note?")) {
      await deleteNote(id);
      const updatedNotes = await getNotesByProjectId(project.id);
      setNotes(updatedNotes);
    }
  };
  // task status
  const handleStatusTaskChange = async (taskId, newStatus) => {
    try {
      await updateTaskStatus(taskId, { status: newStatus });
      setProject((prevProject) => ({
        ...prevProject,
        tasks: prevProject.tasks.map((task) =>
          task.id === taskId ? { ...task, status: newStatus } : task,
        ),
      }));

      setFilteredTasks((prevTasks) =>
        prevTasks.map((task) =>
          task.id === taskId ? { ...task, status: newStatus } : task,
        ),
      );
      alert("Task status updated");
    } catch (err) {
      console.error("Failed to update task status", err);
    }
  };

  // Inside your ProjectDetails component
  const handleBugStatusChange = async (bugId, newStatus) => {
    try {
      await updateBugStatus(bugId, newStatus);
      setProject((prev) => ({
        ...prev,
        bugs: prev.bugs.map((bug) =>
          bug.id === bugId ? { ...bug, status: newStatus } : bug,
        ),
      }));
      setFilteredBugs((prevBugs) =>
        prevBugs.map((bug) =>
          bug.id === bugId ? { ...bug, status: newStatus } : bug,
        ),
      );
      alert("Bug status updated");
    } catch (error) {
      console.error("Failed to update bug status", error);
    }
  };

  // set bug status color
  const bugStatusColors = {
    pending: "border-l-4 pl-4 border-white-600",
    resolved: "border-l-4 pl-4 border-green-600  text-gray-500",
    "in-progress": "border-l-4 pl-4 border-yellow-200",
  };

  // set Task status color
  const taskStatusColors = {
    pending: "border-l-4 pl-4 border-white-600",
    completed: "border-l-4 pl-4 border-green-600  text-gray-500",
    "on-hold": "border-l-4 pl-4 border-red-600  text-white-500",
    "in-progress": "border-l-4 pl-4 border-yellow-200",
  };

  if (loading) return <div className="p-6 text-center">Loading...</div>;
  if (error) return <div className="p-6 text-center text-red-500">{error}</div>;
  if (!project) {
    return (
      <div className="p-6 text-center text-gray-500">
        Project not found or no data available.
      </div>
    );
  }

  return (
    <div className="p-0">
      <Link
        to="/projects"
        className="inline-block mb-6 text-blue-600 hover:underline text-sm"
      >
        ← Back to Projects
      </Link>

      <div className="bg-white p-6 rounded shadow">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">{project.name}</h1>
          <Link
            to={`/projects/edit/${project.id}`}
            className="text-blue-600 hover:underline text-sm"
          >
            ✏️ Edit Project
          </Link>
        </div>

        <div className="space-y-3">
          <p>
            <strong>Client:</strong> {project.clientInfo?.name || "-"}
          </p>
          <p>
            <strong>Status:</strong> {project.status || "-"}
          </p>
          <p>
            <strong>Start Date:</strong> {formatDate(project.startDate)}
          </p>
          <p>
            <strong>Due Date:</strong> {formatDate(project.deadline)}
          </p>
          <div className="mt-4">
            <div className="flex justify-between items-center mb-2">
              <strong>Project Description</strong>
              <button
                onClick={() => setShowDesc(!showDesc)}
                className="text-sm text-blue-600 hover:underline"
              >
                {showDesc ? "Hide" : "Show"}
              </button>
            </div>

            {showDesc && (
              <div className="bg-gray-50 border rounded p-4 prose prose-sm max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {project.description}
                </ReactMarkdown>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tasks */}
      <div className="mt-8 bg-white p-4 rounded shadow">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-xl font-semibold">Related Tasks</h2>

          <div className="flex gap-4 text-sm">
            <Link
              to={`/projects/${project.id}/tasks`}
              className="text-sm text-blue-600 hover:underline"
            >
              View All
            </Link>

            <Link
              to={`/tasks/add?projectId=${project.id}`}
              className="text-blue-600 hover:underline "
            >
              + Add Task
            </Link>
          </div>
        </div>
        {/* Filter Buttons */}

        <div className="flex space-x-2 mb-4 justify-end">
          {["all", "pending", "in-progress", "completed", "on-hold"].map(
            (status) => (
              <button
                key={status}
                onClick={() => setTaskFilter(status)}
                className={`px-3 py-1 rounded text-sm ${
                  taskFilter === status
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700"
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ),
          )}
        </div>

        {/* Filtered Task List */}
        {project.tasks?.length > 0 ? (
          filteredTasks.map((task) => (
            <li
              key={task.id}
              className={`border-b pb-2 flex justify-between items-center hover:bg-slate-50  ${
                taskStatusColors[task.status] || "bg-white"
              }`}
            >
              <div>
                <p className="font-medium">{task.name}</p>
                <p className="text-sm text-gray-500">
                  Deadline:{" "}
                  {task.deadline !== "0000-00-00"
                    ? formatDate(task.deadline)
                    : "Set Due Date"}
                </p>
                {/* Attachments */}
                {task.attachments?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-2 text-xs">
                    {task.attachments.map((att) => (
                      <a
                        key={att.id}
                        href={att.filePath}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline flex items-center gap-1"
                      >
                        📎 Attachment
                      </a>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 ml-4 p-2">
                <select
                  value={task.status}
                  onChange={(e) =>
                    handleStatusTaskChange(task.id, e.target.value)
                  }
                  className="bg-gray-200 text-xs px-2 py-1 rounded-full"
                >
                  <option value="pending">Pending</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="on-hold">On Hold</option>
                </select>

                {/* Icons */}
                <div className="flex gap-3 text-sm">
                  <Link
                    to={`/tasks/edit/${task.id}`}
                    className="text-gray-600 hover:text-green-600"
                    title="Edit Task"
                  >
                    <button className="p-1 hover:bg-gray-100 rounded bg-transparent">
                      <PencilSquareIcon className="h-5 w-5 text-blue-600" />
                    </button>
                  </Link>
                  <Link
                    to={`/tasks/${task.id}`}
                    className="text-gray-600 hover:text-blue-600"
                    title="View Task"
                  >
                    <button className="p-1 hover:bg-gray-100 rounded bg-transparent">
                      <ChevronRightIcon className="h-5 w-5 text-gray-700" />
                    </button>
                  </Link>
                </div>
              </div>
            </li>
          ))
        ) : (
          <p className="text-gray-500">No task found.</p>
        )}
      </div>

      {/* Bugs */}
      <div className="mt-8 bg-white p-4 rounded shadow">
        {/* Header */}
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-xl font-semibold">Related Bugs</h2>

          <div className="flex gap-4 text-sm">
            <Link
              to={`/projects/${project.id}/bugs`}
              className="text-blue-600 hover:underline"
            >
              View All
            </Link>

            <Link
              to={`/bugs/add?projectId=${project.id}`}
              className="text-blue-600 hover:underline"
            >
              + Add Bug
            </Link>
          </div>
        </div>
        {/* Bug Status Filter */}
        <div className="flex gap-2 mb-3 text-sm justify-end">
          {["all", "open", "in-progress", "resolved"].map((status) => (
            <button
              key={status}
              onClick={() => setBugStatusFilter(status)}
              className={`px-3 py-1 rounded capitalize ${
                bugStatusFilter === status
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              {status}
            </button>
          ))}
        </div>

        {/* Bug List */}
        {filteredBugs?.length > 0 ? (
          <ul className="space-y-2">
            {filteredBugs.map((bug) => (
              <li
                key={bug.id}
                className={`border-l-4 pl-4 pr-3 py-2 flex justify-between items-start hover:bg-slate-50 rounded ${
                  bugStatusColors[bug.status] || "border-gray-200"
                }`}
              >
                {/* Left: Bug Info */}
                <div className="flex-1">
                  <p className="font-medium">{bug.title}</p>

                  <p className="text-sm text-gray-500">
                    Priority: {bug.priority} • Reported:{" "}
                    {formatDate(bug.createdAt)}
                  </p>

                  {/* Attachments */}
                  {bug.attachments?.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-2 text-xs">
                      {bug.attachments.map((att) => (
                        <a
                          key={att.id}
                          href={att.filePath}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline flex items-center gap-1"
                        >
                          📎 Attachment
                        </a>
                      ))}
                    </div>
                  )}
                </div>

                {/* Right: Actions */}
                <div className="flex items-center gap-3 ml-4 p-2">
                  {/* Status */}
                  <select
                    value={bug.status}
                    onChange={(e) =>
                      handleBugStatusChange(bug.id, e.target.value)
                    }
                    className="bg-gray-200 text-xs px-2 py-1 rounded-full"
                  >
                    <option value="pending">Pending</option>
                    <option value="in-progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </select>

                  {/* Icons */}
                  <div className="flex gap-3 text-sm">
                    <Link
                      to={`/bugs/${bug.id}/edit/`}
                      className="text-gray-600 hover:text-green-600"
                      title="Edit Bug"
                    >
                      <button className="p-1 hover:bg-gray-100 rounded bg-transparent">
                        <PencilSquareIcon className="h-5 w-5 text-blue-600" />
                      </button>
                    </Link>
                    <Link
                      to={`/bugs/${bug.id}`}
                      className="text-gray-600 hover:text-blue-600"
                      title="View Bug"
                    >
                      <button className="p-1 hover:bg-gray-100 rounded bg-transparent">
                        <ChevronRightIcon className="h-5 w-5 text-gray-700" />
                      </button>
                    </Link>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">No bugs reported.</p>
        )}
      </div>

      {/* Discussion & Approvals */}
      <div className="mt-8 bg-white p-4 rounded shadow">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-xl font-semibold">Discussions</h2>
          <Link
            to={`/projects/${project.id}/discussions`}
            className="text-sm text-blue-600 hover:underline"
          >
            + Add Discussion
          </Link>
        </div>

        {project.discussions?.length > 0 ? (
          <ul className="space-y-3">
            {project.discussions.map((item) => (
              <li
                key={item.id}
                className="border-l-4 pl-4 border-gray-200 bg-slate-50 p-3 rounded"
              >
                <p className="font-medium">{item.title}</p>

                <p className="text-sm text-gray-600">
                  Amount: {formatCurrency(item.amount, item.currency)} |{" "}
                  {item.type} | Date: {item.addedOn}
                </p>

                <p className="text-sm">
                  Approval Status:{" "}
                  {item.status === "approved" ? (
                    <span className="text-green-600 font-semibold">
                      Approved
                    </span>
                  ) : (
                    <span className="text-yellow-600 font-semibold">
                      Pending
                    </span>
                  )}
                </p>

                {item.status === "pending" && (
                  <button
                    onClick={() => handleSendApproval(item.id)}
                    className="text-sm text-blue-600 hover:underline cursor-pointer"
                  >
                    📤 Send Approval Link
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">No discussions yet.</p>
        )}
      </div>
      {/* Extra Work */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-2">
          Extra Work{" "}
          <Link
            to={`/projects/${project.id}/extra-charges`}
            className="text-sm text-blue-600 hover:underline"
          >
            Add New
          </Link>
        </h2>
        {project.extraCharges?.length > 0 ? (
          <ul className="bg-white p-4 rounded shadow space-y-2">
            {project.extraCharges.map((charges) => (
              <li key={charges.id} className="border-b pb-2">
                <p className="font-medium">{charges.title}</p>
                <p className="text-sm text-gray-500">
                  Cost:{" "}
                  {formatCurrency(charges.amount, charges.currency || "INR")} |
                  Date: {charges.addedOn}
                </p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">No extra work entries found.</p>
        )}
      </div>
      {/* Payment Milestones */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-2">
          Payment Milestones{" "}
          <Link
            to={`/projects/${project.id}/milestones`}
            className="text-sm text-blue-600 hover:underline"
          >
            Add New
          </Link>
        </h2>
        {project.paymentMilestones?.length > 0 ? (
          <ul className="bg-white p-4 rounded shadow space-y-2">
            {project.paymentMilestones.map((milestone) => (
              <li key={milestone.id} className="border-b pb-2">
                <p className="font-medium">{milestone.title}</p>
                <p className="text-sm text-gray-500">
                  Cost:{" "}
                  {formatCurrency(
                    milestone.amount,
                    milestone.currency || "INR",
                  )}{" "}
                  | Due Date: {formatDate(milestone.dueDate)}
                </p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">No payment milestone entries found.</p>
        )}
      </div>
      {/* Payments */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-2">
          Paid Payments{" "}
          <Link
            to={`/payments/${project.id}`}
            className="text-sm text-blue-600 hover:underline"
          >
            View All
          </Link>
        </h2>
        {project.payments?.length > 0 ? (
          <ul className="bg-white p-4 rounded shadow space-y-2">
            {project.payments.map((payment) => (
              <li key={payment.id} className="border-b pb-2">
                <p className="font-medium">{payment.title}</p>
                <p className="text-sm text-gray-500">
                  Amount:{" "}
                  {formatCurrency(payment.amount, payment.currency || "INR")} |
                  Date: {formatDate(payment.paymentDate)}
                </p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">No payments recorded.</p>
        )}
      </div>

      {project && (
        <div className="mt-10 bg-white p-6 rounded shadow">
          <h2 className="text-xl font-semibold mb-4">💰 Project Financials</h2>
          <div className="space-y-2 text-gray-800">
            <p>
              <strong>Planned Amount:</strong> ₹{milestoneTotal.toFixed(2)}
            </p>
            <p>
              <strong>Extra Charges:</strong> ₹{extraTotal.toFixed(2)}
            </p>
            <p>
              <strong>Total Expected:</strong> ₹{totalExpected.toFixed(2)}
            </p>
            <p>
              <strong>Total Paid:</strong> ₹{paymentTotal.toFixed(2)}
            </p>
            <p>
              <strong>Pending:</strong> ₹{pending.toFixed(2)}
            </p>
          </div>
        </div>
      )}

      {/* Project Notes */}
      <div className="mt-10">
        <h2 className="text-xl font-semibold mb-4">Project Notes</h2>

        <form onSubmit={handleAddOrUpdateNote} className="mb-4 space-y-2">
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            rows="3"
            className="w-full border p-2 rounded"
            placeholder="Write a note..."
            required
          />
          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-1 rounded hover:bg-blue-700"
          >
            {editingNote ? "Update Note" : "Add Note"}
          </button>
          {editingNote && (
            <button
              type="button"
              onClick={() => {
                setNoteText("");
                setEditingNote(null);
              }}
              className="ml-2 text-gray-500 hover:underline"
            >
              Cancel
            </button>
          )}
        </form>

        {notes.length > 0 ? (
          <ul className="bg-yellow-50 border rounded shadow divide-y">
            {notes.map((note) => (
              <li
                key={note.id}
                className="p-3 flex justify-between items-start"
              >
                <div>
                  <p className="text-gray-800">{note.note}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(note.addedOn).toLocaleString()}
                  </p>
                </div>
                <div className="flex space-x-2 ml-4">
                  <button
                    onClick={() => handleEdit(note)}
                    className="text-blue-600 text-sm hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(note.id)}
                    className="text-red-600 text-sm hover:underline"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">No notes yet.</p>
        )}
      </div>
    </div>
  );
};

export default ProjectDetail;
